import {
  BannerInterface,
  BannerListInterface,
  DashboardEventInterface,
  DashboardInterface,
} from '@elearning-platform/api-types/interfaces';
import {
  AnchorPanelSection,
  Banner,
  Breakpoint,
  Card,
  CardGroupCard,
  Color,
  EventTileDataType,
  EventTileDetailedData,
  EventTileLayout,
  getPathWithParams,
  HelpBanner,
  HelpBannerType,
  ItemsCarousel,
  SliderBanner,
  useFormatDate,
  UseFormatDate,
} from '@elearning-platform/ui';
import { UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query';
import React, { RefObject, useCallback, useMemo, useRef } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { getBanners, getInitialData } from '../../api/requests';
import { AppConfig } from '../../appConfig';
import { QueryKey, RouteNameEnum, ViewImage, ViewSearchParam } from '../../enums';
import { useAuth, useEventDisenroll, useLessonCard, useRedirectTo } from '../../hooks';
import { UseAuth, UseLessonCard, UseRedirectTo } from '../../interfaces';
import { getUrlByRouteName } from '../../utils';

import { Header } from './components';
import { Container, RequiredLessonsWrapper, StyledEventCard, Wrapper } from './dashboardV2.styled';

const DashboardV2: React.FC = (): JSX.Element => {
  const { t }: TransProps<never> = useTranslation();
  const isTablet: boolean = useMediaQuery({
    query: Breakpoint.Tablet,
  });
  const isSmallDesktop: boolean = useMediaQuery({
    query: Breakpoint.SmallDesktop,
  });
  const { transformThumbnailIntoCardData }: UseLessonCard = useLessonCard();
  const { getTimestampMilisecValue }: UseFormatDate = useFormatDate();
  const { redirect }: UseRedirectTo = useRedirectTo();
  const { isLoggedIn }: UseAuth = useAuth();

  const requiredLessonRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const recommendedEventsRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  const { data }: UseQueryResult<DashboardInterface> = useQuery({
    queryKey: [QueryKey.DashboardData],
    queryFn: (): Promise<DashboardInterface> => getInitialData(),
    enabled: isLoggedIn,
  });
  const { data: banners }: UseQueryResult<BannerListInterface> = useQuery({
    queryKey: [QueryKey.BannersData],
    queryFn: (): Promise<BannerListInterface> => getBanners(),
    enabled: isLoggedIn,
  });

  const {
    mutate: disenroll,
    isPending: isDisenrollPending,
    variables: disenrollVariables,
  }: UseMutationResult<void, Error, number> = useEventDisenroll();

  const transformBannerDataIntoBanners: (banner: BannerInterface) => Banner = useCallback((banner: BannerInterface): Banner => ({
    badgeText: banner.headerText ?? undefined,
    image: !isTablet
      ? banner.imagePhoneUrl
      : !isSmallDesktop
          ? banner.imageTabletUrl
          : banner.imageDesktopUrl,
    path: banner.url ?? undefined,
    title: banner.mainText ?? undefined,
    buttonLabel: banner.buttonText ?? undefined,
  }), [isSmallDesktop, isTablet]);

  const sectionsData: AnchorPanelSection[] = [
    { ref: requiredLessonRef, text: t('competence.app.dashboard.header.requiredLessons') },
    { ref: recommendedEventsRef, text: t('competence.app.dashboard.header.comingEvents') },
  ];

  const transformedBanners: Banner[] = useMemo((): Banner[] => (
    banners?.data.map((banner: BannerInterface): Banner => transformBannerDataIntoBanners(banner)) || []
  ), [banners?.data, transformBannerDataIntoBanners]);

  const transformEventToCard: (item: DashboardEventInterface) => EventTileDetailedData | null = useCallback(
    (item: DashboardEventInterface): EventTileDetailedData | null => {
      if (item.timestampStart === null || item.timestampEnd === null) {
        return null;
      }

      return {
        id: item.id,
        name: item.course.name,
        type: EventTileDataType.Detailed,
        layout: EventTileLayout.Vertical,
        date: {
          from: getTimestampMilisecValue(item.timestampStart),
          to: getTimestampMilisecValue(item.timestampEnd),
        },
        isAttending: item.userFlags.isEnrolled,
        eventType: item.course.category?.enum === 'exams' ? 'exam' : 'event',
        location: item.city ?? undefined,
        img: item.course.image ?? ViewImage.TrainingsDefaultThumbnail,
        actions: {
          primaryButtonData: {
            label: item.userFlags?.isEnrolled
              ? t('competence.app.dashboard.eventTile.disenroll')
              : t('competence.app.dashboard.eventTile.enroll'),
            isLoading: !!(isDisenrollPending && disenrollVariables === item.id),
            onClick: (): void => item.userFlags?.isEnrolled
              ? disenroll(item.id)
              : redirect({ route: RouteNameEnum.Trainings, searchParams: { [ViewSearchParam.TrainingId]: item.id } }),
          },
          secondaryButtonData: ((item.userFlags?.isEnrolled && item.timestampStart)
          && getTimestampMilisecValue(item.timestampStart) > Date.now())
            ? { onClick: (): void => redirect({ route: RouteNameEnum.Trainings, searchParams: { [ViewSearchParam.TrainingId]: item.id } }) }
            : undefined,
          detailsLinkData: {
            internalPath: getPathWithParams(getUrlByRouteName(RouteNameEnum.TrainingDetails)?.url ?? '', { id: item.id.toString() }),
          },
        },
      };
    },
    [disenrollVariables, disenroll, isDisenrollPending, t, redirect, getTimestampMilisecValue]
  );

  const transformedEvents: EventTileDetailedData[] = useMemo((): EventTileDetailedData[] => {
    if (!data?.events.recommended) {
      return [];
    }

    return data.events.recommended
      .map(transformEventToCard)
      .filter((card: EventTileDetailedData | null): card is EventTileDetailedData => card !== null);
  }, [data?.events.recommended, transformEventToCard]);

  return (
    <>
      <Header
        sections={sectionsData}
        statistics={data?.statistics}
        upcomingEvent={data?.events.incoming ?? undefined}
      />
      <Wrapper>
        {!!transformedBanners.length && (
          <SliderBanner banners={transformedBanners} duration={5000} />
        )}
        <RequiredLessonsWrapper ref={requiredLessonRef}>
          {!!data && (
            <ItemsCarousel
              emptyPlaceholderData={{
                text: t('competence.app.dashboard.emptyLessons'),
              }}
              headlineData={{
                headline: t('competence.app.dashboard.headline.requiredLessons'),
              }}
              items={data.content.required.map(transformThumbnailIntoCardData)}
              renderItem={(item: CardGroupCard): JSX.Element => (
                <Card
                  {...item}
                  tagsData={{
                    list: item.tags,
                    colorsData: {
                      backgroundColor: Color.White,
                      borderColor: Color.Gray300,
                      textColor: Color.Gray600,
                    },
                  }}
                />
              )}
            />
          )}
        </RequiredLessonsWrapper>
        <Container ref={recommendedEventsRef}>
          <ItemsCarousel
            emptyPlaceholderData={{
              text: t('competence.app.dashboard.emptyEvents'),
            }}
            headlineData={{
              headline: t('competence.app.dashboard.headline.events'),
            }}
            items={transformedEvents}
            renderItem={(item: EventTileDetailedData): JSX.Element => (
              <StyledEventCard data={item} />
            )}
          />
        </Container>
      </Wrapper>
      <HelpBanner
        data={{
          type: HelpBannerType.Contact,
          bgImage: isTablet
            ? ViewImage.CommonHelpBannerBg
            : ViewImage.CommonHelpBannerMobileBg,
          text: t('competence.app.global.helpBanner.needHelp'),
          buttonData: {
            label: t('competence.app.global.helpBanner.help'),
            externalUrl: `mailto:${AppConfig.platformContactEmail}`,
          },
        }}
      />
    </>
  );
};

export default DashboardV2;
